// Placement for a workspace that has no collision-free slot of the requested
// size. Opening a window must never minimize or move the windows a person
// already has open, so the only levers are the new window's own size and its
// stacking order.

import { findLeastOverlappingWindowPosition } from "@smartmanager/shell-ui/features/windows/windowPosition";
import {
  occupancyRect,
  rectsOverlap,
  type LayoutPoint,
  type LayoutRect,
  type LayoutSize,
} from "@/src/state/appStore/layoutGeometry";

/** A fitted window keeps at least this share of each requested dimension. */
export const DEFAULT_MIN_SHRINK_RATIO = 0.85;

/**
 * Overlap cost of an attention prompt relative to an ordinary window. Covering
 * one pixel of a prompt costs as much as covering this many pixels of ordinary
 * content, so the fallback covers a prompt only when every position does.
 */
export const ATTENTION_PROMPT_OVERLAP_WEIGHT = 1000;

/** A visible window's rect, plus how costly it is to cover. */
export type SaturatedObstacle = LayoutRect & {
  /** Defaults to 1. See ATTENTION_PROMPT_OVERLAP_WEIGHT. */
  overlapWeight?: number;
};

export type SaturatedPlacementInput = {
  requestedSize: LayoutSize;
  screen: LayoutSize;
  /** Visible windows' rects, without the occupancy margin. */
  obstacles: SaturatedObstacle[];
  /**
   * App chrome no window may cover (workspaceChrome.ts), with no margin of
   * its own. A shrink-fit region never includes it, and the full-size
   * fallback covers any amount of window content before it covers chrome.
   */
  reserved?: LayoutRect[];
  /** Top-left point the window would ideally use. */
  preferred: LayoutPoint;
  margin: number;
  /**
   * Whether the new window may open slightly smaller to fit a free region.
   * Callers disable it for windows with a fixed layout contract (prompts,
   * restored geometry, dependencies).
   */
  allowShrink: boolean;
  minShrinkRatio?: number;
};

export type SaturatedPlacement = {
  position: LayoutPoint;
  size: LayoutSize;
  /** True when the window covers another one; the caller stacks it on top. */
  overlapsExisting: boolean;
};

const toOccupied = (rect: LayoutRect, margin: number): LayoutRect =>
  occupancyRect({ x: rect.x, y: rect.y }, { width: rect.width, height: rect.height }, margin);

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/**
 * Exact maximal free rectangles of the screen. Coordinates are compressed to
 * the obstacle edges, so every free cell is either fully free or fully
 * covered, and a histogram sweep over the compressed rows yields each free
 * rectangle that cannot grow sideways. A coarse pixel grid would round every
 * obstacle outwards and lose the few pixels that decide whether a window fits.
 */
export function findFreeRectangles(occupied: LayoutRect[], screen: LayoutSize): LayoutRect[] {
  const edges = (start: (rect: LayoutRect) => number, extent: (rect: LayoutRect) => number, limit: number) =>
    Array.from(
      new Set([
        0,
        limit,
        ...occupied.flatMap((rect) => [
          clamp(start(rect), 0, limit),
          clamp(start(rect) + extent(rect), 0, limit),
        ]),
      ]),
    ).sort((a, b) => a - b);
  const xs = edges((rect) => rect.x, (rect) => rect.width, screen.width);
  const ys = edges((rect) => rect.y, (rect) => rect.height, screen.height);
  const columns = xs.length - 1;
  if (columns <= 0 || ys.length < 2) return [];

  const heights = new Array<number>(columns).fill(0);
  const rectangles: LayoutRect[] = [];
  for (let row = 0; row < ys.length - 1; row++) {
    const top = ys[row];
    const bottom = ys[row + 1];
    for (let column = 0; column < columns; column++) {
      const left = xs[column];
      const right = xs[column + 1];
      const covered = occupied.some((rect) =>
        rect.x < right && rect.x + rect.width > left && rect.y < bottom && rect.y + rect.height > top,
      );
      heights[column] = covered ? 0 : heights[column] + (bottom - top);
    }

    const stack: Array<{ column: number; height: number }> = [];
    for (let column = 0; column <= columns; column++) {
      const height = column < columns ? heights[column] : 0;
      let startColumn = column;
      while (stack.length > 0 && stack[stack.length - 1].height >= height) {
        const popped = stack.pop()!;
        startColumn = popped.column;
        if (popped.height > 0) {
          rectangles.push({
            x: xs[popped.column],
            y: bottom - popped.height,
            width: xs[column] - xs[popped.column],
            height: popped.height,
          });
        }
      }
      if (height > 0) stack.push({ column: startColumn, height });
    }
  }
  return rectangles;
}

function findShrinkFit(
  input: SaturatedPlacementInput,
  minShrinkRatio: number,
): SaturatedPlacement | null {
  const { requestedSize, screen, obstacles, preferred, margin } = input;
  const minWidth = Math.ceil(requestedSize.width * minShrinkRatio);
  const minHeight = Math.ceil(requestedSize.height * minShrinkRatio);
  // Free regions are computed against margin-expanded obstacles; the new
  // window's own margin is then reserved inside the region, which matches the
  // quadtree occupancy check used by ordinary placement.
  const occupied = [
    ...obstacles.map((rect) => toOccupied(rect, margin)),
    ...(input.reserved ?? []),
  ];
  const regions = findFreeRectangles(occupied, screen);

  let best: { placement: SaturatedPlacement; area: number; distance: number } | null = null;
  for (const region of regions) {
    // Integer bounds inside the region, with the new window's own margin.
    const left = Math.ceil(region.x + margin);
    const top = Math.ceil(region.y + margin);
    const right = Math.floor(region.x + region.width - margin);
    const bottom = Math.floor(region.y + region.height - margin);
    const width = Math.min(requestedSize.width, right - left);
    const height = Math.min(requestedSize.height, bottom - top);
    if (width < minWidth || height < minHeight) continue;

    const position = {
      x: clamp(Math.round(preferred.x), left, right - width),
      y: clamp(Math.round(preferred.y), top, bottom - height),
    };
    const area = width * height;
    const distance = Math.hypot(position.x - preferred.x, position.y - preferred.y);
    if (
      !best ||
      area > best.area ||
      (area === best.area && distance < best.distance)
    ) {
      best = {
        placement: { position, size: { width, height }, overlapsExisting: false },
        area,
        distance,
      };
    }
  }

  return best?.placement ?? null;
}

/**
 * Chooses where a new window goes once collision-free placement at the
 * requested size is exhausted.
 *
 * 1. If shrinking is allowed, use the largest free region that keeps at least
 *    `minShrinkRatio` of each requested dimension (ties go to the region
 *    closest to the preferred point).
 * 2. Otherwise, open at full size where it covers the least existing content,
 *    weighted by each obstacle's overlapWeight, and never over reserved
 *    chrome while any position avoids it. The caller raises it above the
 *    ordinary windows it overlaps.
 */
export function placeInSaturatedWorkspace(
  input: SaturatedPlacementInput,
): SaturatedPlacement {
  const minShrinkRatio = input.minShrinkRatio ?? DEFAULT_MIN_SHRINK_RATIO;
  if (input.allowShrink) {
    const fitted = findShrinkFit(input, minShrinkRatio);
    if (fitted) return fitted;
  }

  const position = findLeastOverlappingWindowPosition(
    input.requestedSize,
    input.screen,
    [
      ...input.obstacles.map((rect) => ({
        position: { x: rect.x, y: rect.y },
        size: { width: rect.width, height: rect.height },
        overlapWeight: rect.overlapWeight,
      })),
      ...(input.reserved ?? []).map((rect) => ({
        position: { x: rect.x, y: rect.y },
        size: { width: rect.width, height: rect.height },
        keepOut: true,
      })),
    ],
    input.preferred,
    input.margin,
  );
  const placedRect = occupancyRect(position, input.requestedSize, input.margin);
  return {
    position,
    size: { ...input.requestedSize },
    overlapsExisting: input.obstacles.some((rect) =>
      rectsOverlap(placedRect, toOccupied(rect, input.margin)),
    ),
  };
}
